import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  Search, CalendarDays, Clock, User, ChevronLeft, ChevronRight,
  Coffee, Briefcase, Shield, AlertTriangle, Check
} from 'lucide-react';
import { toast } from 'sonner';

function timeToMinutes(t) {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function formatTime12(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function formatDateLabel(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function isWeekend(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return d.getDay() === 0 || d.getDay() === 6;
}

const BLOCK_ICONS = {
  lunch: Coffee,
  admin: Briefcase,
  meeting: CalendarDays,
  off: Shield,
};

const BLOCK_STYLES = {
  lunch: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
  admin: { bg: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-200' },
  meeting: { bg: 'bg-teal-50', text: 'text-teal-600', border: 'border-teal-200' },
  off: { bg: 'bg-gray-50', text: 'text-gray-500', border: 'border-gray-200' },
};

// ============================================================
// MINI SCHEDULE PREVIEW
// ============================================================

function MiniSchedulePreview({
  providerId,
  providerName,
  providerColor,
  dateStr,
  appointments,
  template,
  availableSlots,
  bookedSlots,
  selectedTime,
  selectedDuration,
  onSlotClick,
}) {
  const startHour = 8;
  const endHour = 17;

  // Build slot rows
  const slotRows = useMemo(() => {
    const rows = [];
    const dow = dateStr ? (() => {
      const d = new Date(dateStr + 'T12:00:00');
      return d.getDay() === 0 ? 7 : d.getDay();
    })() : 1;

    const blocks = template?.blocks?.filter(b => b.day_of_week === dow) || [];

    for (let h = startHour; h < endHour; h++) {
      for (let m = 0; m < 60; m += 15) {
        const time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        const mins = h * 60 + m;

        // Check if in a non-available block
        let blockInfo = null;
        for (const block of blocks) {
          const bStart = timeToMinutes(block.start_time);
          const bEnd = timeToMinutes(block.end_time);
          if (mins >= bStart && mins < bEnd && block.block_type !== 'available') {
            blockInfo = { type: block.block_type, label: block.label || block.block_type };
            break;
          }
        }

        // Check if booked
        const appt = appointments.find(a => {
          if (a.status === 'cancelled' || a.status === 'no_show') return false;
          const aStart = timeToMinutes(a.time);
          const aEnd = aStart + (a.duration_minutes || 15);
          return mins >= aStart && mins < aEnd;
        });

        // Check if this is a start of an appointment
        const isApptStart = appt && appt.time === time;

        const isAvailable = availableSlots.includes(time) && !blockInfo && !appt;
        const isBooked = bookedSlots.includes(time) || !!appt;
        const isSelected = selectedTime === time;

        // Check if selected time would overlap
        let isInSelectedRange = false;
        if (selectedTime && selectedDuration) {
          const selStart = timeToMinutes(selectedTime);
          const selEnd = selStart + selectedDuration;
          isInSelectedRange = mins >= selStart && mins < selEnd;
        }

        rows.push({
          time,
          mins,
          blockInfo,
          appt: isApptStart ? appt : null,
          apptContinuation: appt && !isApptStart,
          isAvailable,
          isBooked,
          isSelected,
          isInSelectedRange,
          isHourMark: m === 0,
        });
      }
    }
    return rows;
  }, [dateStr, appointments, template, availableSlots, bookedSlots, selectedTime, selectedDuration]);

  // Group consecutive non-available block rows
  const renderRows = useMemo(() => {
    const result = [];
    let i = 0;
    while (i < slotRows.length) {
      const row = slotRows[i];

      // If it's a block start, find the end
      if (row.blockInfo && (i === 0 || !slotRows[i - 1].blockInfo || slotRows[i - 1].blockInfo.type !== row.blockInfo.type)) {
        let j = i;
        while (j < slotRows.length && slotRows[j].blockInfo?.type === row.blockInfo.type) j++;
        const spanSlots = j - i;
        result.push({ type: 'block', blockInfo: row.blockInfo, startTime: row.time, endTime: slotRows[j - 1] ? (() => { const m = slotRows[j - 1].mins + 15; return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`; })() : '', span: spanSlots });
        i = j;
        continue;
      }

      // If it's an appointment start, show it
      if (row.appt) {
        const dur = row.appt.duration_minutes || 15;
        const span = Math.max(1, dur / 15);
        result.push({ type: 'appointment', appt: row.appt, startTime: row.time, span });
        i += span;
        continue;
      }

      // If it's a continuation of appointment or block, skip
      if (row.apptContinuation || (row.blockInfo)) {
        i++;
        continue;
      }

      // Available slot
      result.push({ type: 'slot', ...row, span: 1 });
      i++;
    }
    return result;
  }, [slotRows]);

  return (
    <div className="flex flex-col h-full" data-testid="mini-schedule-preview">
      {/* Preview Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30 shrink-0">
        <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: providerColor || '#6B7280' }} />
        <span className="text-xs font-medium truncate">{providerName || 'Select provider'}</span>
      </div>
      <div className="px-3 py-1.5 border-b bg-muted/20 shrink-0">
        <span className="text-[10px] text-muted-foreground">{formatDateLabel(dateStr)}</span>
      </div>

      {/* Schedule Grid */}
      <ScrollArea className="flex-1">
        <div className="px-1 py-1">
          {!providerId ? (
            <div className="flex items-center justify-center h-40 text-xs text-muted-foreground">
              Select a provider to see their schedule
            </div>
          ) : renderRows.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-xs text-muted-foreground">
              No schedule data for this date
            </div>
          ) : (
            renderRows.map((row, idx) => {
              if (row.type === 'block') {
                const style = BLOCK_STYLES[row.blockInfo.type] || BLOCK_STYLES.off;
                const Icon = BLOCK_ICONS[row.blockInfo.type] || Shield;
                return (
                  <div
                    key={`block-${idx}`}
                    className={`flex items-center gap-1.5 px-2 rounded-md border ${style.bg} ${style.border} ${style.text} my-0.5`}
                    style={{ height: `${Math.max(row.span * 22, 28)}px` }}
                  >
                    <span className="text-[9px] tabular-nums w-12 shrink-0 opacity-60">
                      {formatTime12(row.startTime)}
                    </span>
                    <Icon className="w-3 h-3 shrink-0" />
                    <span className="text-[10px] font-medium truncate">
                      {row.blockInfo.label}
                    </span>
                    <span className="text-[9px] ml-auto opacity-60 tabular-nums shrink-0">
                      {formatTime12(row.endTime)}
                    </span>
                  </div>
                );
              }

              if (row.type === 'appointment') {
                const a = row.appt;
                return (
                  <div
                    key={`appt-${idx}`}
                    className="flex items-center gap-1.5 px-2 rounded-md border bg-blue-50 border-blue-200 my-0.5"
                    style={{ height: `${Math.max(row.span * 22, 28)}px` }}
                  >
                    <span className="text-[9px] tabular-nums w-12 shrink-0 text-blue-500">
                      {formatTime12(row.startTime)}
                    </span>
                    <User className="w-3 h-3 text-blue-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-[10px] font-medium text-blue-700 truncate block">
                        {a.patient_name}
                      </span>
                      <span className="text-[9px] text-blue-500 truncate block">
                        {a.visit_type} ({a.duration_minutes}m)
                      </span>
                    </div>
                    <Badge variant="outline" className="text-[8px] h-4 px-1 border-blue-200 text-blue-600 shrink-0">
                      {a.status}
                    </Badge>
                  </div>
                );
              }

              // Available slot
              const isInRange = row.isInSelectedRange && !row.isSelected;
              return (
                <TooltipProvider key={`slot-${idx}`} delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className={`w-full flex items-center gap-1.5 px-2 rounded-md border my-0.5 transition-colors
                          ${row.isSelected
                            ? 'bg-primary/10 border-primary/40 ring-1 ring-primary/30'
                            : isInRange
                              ? 'bg-primary/5 border-primary/20'
                              : row.isHourMark
                                ? 'bg-background border-border/60 hover:bg-accent/60 hover:border-primary/30'
                                : 'bg-background border-border/30 hover:bg-accent/40 hover:border-primary/20'
                          }`}
                        style={{ height: '22px' }}
                        onClick={() => row.isAvailable && onSlotClick(row.time)}
                        disabled={!row.isAvailable}
                        data-testid={`preview-slot-${row.time}`}
                      >
                        <span className={`text-[9px] tabular-nums w-12 shrink-0 ${
                          row.isSelected ? 'text-primary font-bold' : row.isHourMark ? 'text-foreground/70 font-medium' : 'text-muted-foreground/60'
                        }`}>
                          {row.isHourMark ? formatTime12(row.time) : ''}
                        </span>
                        {row.isSelected && (
                          <span className="text-[10px] text-primary font-medium flex items-center gap-1">
                            <Check className="w-3 h-3" /> Selected
                          </span>
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="text-xs">
                      {row.isAvailable ? `Click to book at ${formatTime12(row.time)}` : 'Not available'}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              );
            })
          )}
        </div>
      </ScrollArea>

      {/* Legend */}
      <div className="flex items-center gap-3 px-3 py-1.5 border-t bg-muted/20 shrink-0 flex-wrap">
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 rounded-sm bg-blue-50 border border-blue-200" />
          <span className="text-[9px] text-muted-foreground">Booked</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 rounded-sm bg-background border border-border" />
          <span className="text-[9px] text-muted-foreground">Open</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-2.5 h-2.5 rounded-sm bg-amber-50 border border-amber-200" />
          <span className="text-[9px] text-muted-foreground">Blocked</span>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MAIN DIALOG
// ============================================================

export default function NewAppointmentDialog({
  open,
  onOpenChange,
  providers,
  visitTypes,
  initialDate,
  initialTime,
  initialProviderId,
  onCreated,
  currentUser,
}) {
  const [form, setForm] = useState({
    patient_id: '',
    provider_id: '',
    date: '',
    time: '',
    visit_type: '',
    duration_minutes: 15,
    notes: '',
  });
  const [patientSearch, setPatientSearch] = useState('');
  const [patients, setPatients] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Preview data
  const [previewAppts, setPreviewAppts] = useState([]);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Set initial date and auto-select provider when dialog opens
  useEffect(() => {
    if (open) {
      // Determine default provider:
      // 1. If current user IS a provider, auto-select themselves
      // 2. Otherwise, use last chosen provider from localStorage
      let defaultProviderId = '';
      if (currentUser?.role === 'provider' && providers?.length) {
        const selfProvider = providers.find(p =>
          p.name === currentUser.name || p.user_id === currentUser.user_id
        );
        if (selfProvider) defaultProviderId = selfProvider.id;
      }
      if (!defaultProviderId) {
        const lastChosen = localStorage.getItem('hfc_last_provider');
        if (lastChosen && providers?.some(p => p.id === lastChosen)) {
          defaultProviderId = lastChosen;
        }
      }

      setForm(f => ({
        ...f,
        date: initialDate || new Date().toISOString().split('T')[0],
        patient_id: '',
        provider_id: initialProviderId || defaultProviderId,
        time: initialTime || '',
        visit_type: '',
        duration_minutes: 15,
        notes: '',
      }));
      if (initialTime) skipNextTimeClear.current = true;
      setPatientSearch('');
      setPatients([]);
      setError('');
      // Only clear slots if provider or date actually changed
      if (!initialProviderId || !form.provider_id || initialProviderId !== form.provider_id) {
        setPreviewAppts([]);
        setPreviewTemplate(null);
        setAvailableSlots([]);
        setBookedSlots([]);
      }
    }
  }, [open, initialDate, initialProviderId, initialTime, currentUser, providers]);

  // Fetch preview when provider or date changes
  useEffect(() => {
    if (!form.provider_id || !form.date) {
      setPreviewAppts([]);
      setAvailableSlots([]);
      setBookedSlots([]);
      return;
    }

    let cancelled = false;
    const fetchPreview = async () => {
      setPreviewLoading(true);
      try {
        const [schedRes, slotsRes, tmplRes] = await Promise.all([
          api.get(`/appointments/schedule?date=${form.date}&view=day`),
          api.get(`/appointments/available-slots?provider_id=${form.provider_id}&date=${form.date}`),
          api.get(`/schedule-templates/${form.provider_id}`).catch(() => ({ data: { template: null } })),
        ]);
        if (cancelled) return;
        const provAppts = (schedRes.data.appointments || []).filter(a => a.provider_id === form.provider_id);
        setPreviewAppts(provAppts);
        setAvailableSlots(slotsRes.data.slots || []);
        setBookedSlots(slotsRes.data.booked || []);
        setPreviewTemplate(tmplRes.data.template || tmplRes.data.schedule_template);
      } catch (err) {
        if (!cancelled) {
          setPreviewAppts([]);
          setAvailableSlots([]);
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };

    fetchPreview();
    return () => { cancelled = true; };
  }, [form.provider_id, form.date]);

  // Clear time when provider or date changes (slot may no longer be available)
  // BUT: skip clearing if time was just set from initialTime (dialog just opened)
  const skipNextTimeClear = React.useRef(false);
  useEffect(() => {
    if (skipNextTimeClear.current) {
      skipNextTimeClear.current = false;
      return;
    }
    setForm(f => ({ ...f, time: '' }));
  }, [form.provider_id, form.date]);

  const searchPatients = async (query) => {
    if (query.length < 2) { setPatients([]); return; }
    try {
      const res = await api.get(`/patients?search=${query}`);
      setPatients(res.data.patients || []);
    } catch (err) { /* ignore */ }
  };

  const selectedProvider = useMemo(() =>
    providers?.find(p => p.id === form.provider_id),
    [providers, form.provider_id]
  );

  const selectedVt = useMemo(() =>
    visitTypes?.find(v => v.name === form.visit_type),
    [visitTypes, form.visit_type]
  );

  // Filter time options to only available slots
  const timeOptions = useMemo(() => {
    if (!availableSlots.length) return [];
    return availableSlots.map(t => ({
      value: t,
      label: formatTime12(t),
      available: true,
    }));
  }, [availableSlots]);

  const changeDate = (offset) => {
    if (!form.date) return;
    const d = new Date(form.date + 'T12:00:00');
    d.setDate(d.getDate() + offset);
    setForm(f => ({ ...f, date: d.toISOString().split('T')[0] }));
  };

  const handleSubmit = async () => {
    setError('');
    if (!form.patient_id || !form.provider_id || !form.time || !form.visit_type || !form.date) {
      setError('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/appointments', {
        patient_id: form.patient_id,
        provider_id: form.provider_id,
        provider_name: selectedProvider?.name || '',
        date: form.date,
        time: form.time,
        visit_type: form.visit_type,
        duration_minutes: selectedVt?.duration_minutes || form.duration_minutes,
        notes: form.notes,
      });
      toast.success('Appointment scheduled');
      // Remember last chosen provider for next time
      if (form.provider_id) {
        localStorage.setItem('hfc_last_provider', form.provider_id);
      }
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to create appointment';
      setError(detail);
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSlotClick = (time) => {
    setForm(f => ({ ...f, time }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl p-0 gap-0 overflow-hidden" data-testid="new-appointment-dialog">
        <div className="flex h-[600px]">
          {/* LEFT: Booking Form */}
          <div className="flex-1 flex flex-col min-w-0">
            <DialogHeader className="px-5 pt-4 pb-3 border-b shrink-0">
              <DialogTitle className="text-sm" style={{ fontFamily: 'Space Grotesk' }}>
                Schedule Appointment
              </DialogTitle>
              <DialogDescription className="text-xs">
                Select a provider and date to see available slots
              </DialogDescription>
            </DialogHeader>

            <ScrollArea className="flex-1">
              <div className="px-5 py-4 space-y-3">
                {error && (
                  <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2 flex items-center gap-1.5" data-testid="appointment-error">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    {error}
                  </div>
                )}

                {/* Patient Search */}
                <div>
                  <Label className="text-xs font-medium">Patient *</Label>
                  <div className="relative mt-1">
                    <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or MRN..."
                      className="pl-8 h-8 text-xs"
                      value={patientSearch}
                      onChange={e => { setPatientSearch(e.target.value); searchPatients(e.target.value); }}
                      data-testid="appointment-patient-search"
                    />
                  </div>
                  {patients.length > 0 && (
                    <div className="border rounded-md mt-1 max-h-28 overflow-auto bg-card shadow-sm">
                      {patients.map(p => (
                        <button
                          key={p.id}
                          className={`w-full text-left px-3 py-1.5 text-xs hover:bg-accent transition-colors
                            ${form.patient_id === p.id ? 'bg-accent font-medium' : ''}`}
                          onClick={() => {
                            setForm(f => ({ ...f, patient_id: p.id }));
                            setPatientSearch(`${p.name?.first} ${p.name?.last}`);
                            setPatients([]);
                          }}
                          data-testid={`patient-option-${p.mrn}`}
                        >
                          {p.name?.first} {p.name?.last}
                          <span className="text-muted-foreground ml-1">{p.mrn}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Provider */}
                <div>
                  <Label className="text-xs font-medium">Provider *</Label>
                  <Select value={form.provider_id} onValueChange={v => setForm(f => ({ ...f, provider_id: v }))}>
                    <SelectTrigger className="h-8 text-xs mt-1" data-testid="appointment-provider-select">
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      {providers?.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          <span className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
                            {p.name}
                            <span className="text-muted-foreground text-[10px]">{p.specialty}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date Picker */}
                <div>
                  <Label className="text-xs font-medium">Date *</Label>
                  <div className="flex items-center gap-1 mt-1">
                    <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" onClick={() => changeDate(-1)} data-testid="date-prev">
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </Button>
                    <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="flex-1 h-8 text-xs justify-start gap-2 font-normal tabular-nums"
                          data-testid="appointment-date-picker"
                        >
                          <CalendarDays className="w-3.5 h-3.5 text-muted-foreground" />
                          {form.date ? formatDateLabel(form.date) : 'Pick a date'}
                          {form.date && isWeekend(form.date) && (
                            <Badge variant="outline" className="text-[8px] h-3.5 px-1 bg-amber-50 text-amber-600 border-amber-200 ml-auto">Weekend</Badge>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={form.date ? new Date(form.date + 'T12:00:00') : undefined}
                          onSelect={(d) => {
                            if (d) {
                              const str = d.toISOString().split('T')[0];
                              setForm(f => ({ ...f, date: str }));
                              setCalendarOpen(false);
                            }
                          }}
                          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                        />
                      </PopoverContent>
                    </Popover>
                    <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" onClick={() => changeDate(1)} data-testid="date-next">
                      <ChevronRight className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>

                {/* Visit Type */}
                <div>
                  <Label className="text-xs font-medium">Visit Type *</Label>
                  <Select
                    value={form.visit_type}
                    onValueChange={v => {
                      const vt = visitTypes?.find(x => x.name === v);
                      setForm(f => ({ ...f, visit_type: v, duration_minutes: vt?.duration_minutes || 15 }));
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs mt-1" data-testid="appointment-visit-type">
                      <SelectValue placeholder="Select visit type" />
                    </SelectTrigger>
                    <SelectContent>
                      {visitTypes?.map(vt => (
                        <SelectItem key={vt.id} value={vt.name}>
                          <span className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: vt.color }} />
                            {vt.name}
                            <span className="text-muted-foreground text-[10px] ml-1">{vt.duration_minutes}min</span>
                            {vt.allow_double_book && (
                              <Badge variant="outline" className="text-[8px] h-3.5 px-1 ml-1">2x</Badge>
                            )}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Time + Duration */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs font-medium">Time *</Label>
                    {form.provider_id && form.date ? (
                      <Select value={form.time} onValueChange={v => setForm(f => ({ ...f, time: v }))}>
                        <SelectTrigger className="h-8 text-xs mt-1 tabular-nums" data-testid="appointment-time-select">
                          <SelectValue placeholder={availableSlots.length ? 'Select time' : 'No slots'} />
                        </SelectTrigger>
                        <SelectContent className="max-h-48">
                          {timeOptions.map(t => (
                            <SelectItem key={t.value} value={t.value} className="tabular-nums text-xs">
                              {t.label}
                            </SelectItem>
                          ))}
                          {timeOptions.length === 0 && (
                            <div className="px-3 py-2 text-xs text-muted-foreground">No available slots</div>
                          )}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="h-8 mt-1 flex items-center px-3 bg-muted/50 rounded-md border text-xs text-muted-foreground">
                        Select provider & date first
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs font-medium">Duration</Label>
                    <div className="h-8 mt-1 flex items-center px-3 bg-muted/50 rounded-md border text-xs tabular-nums">
                      {selectedVt ? `${selectedVt.duration_minutes} min` : `${form.duration_minutes} min`}
                    </div>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label className="text-xs font-medium">Notes</Label>
                  <Input
                    className="h-8 text-xs mt-1"
                    value={form.notes}
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Optional notes..."
                    data-testid="appointment-notes-input"
                  />
                </div>

                {/* Selected time hint */}
                {form.time && form.provider_id && (
                  <div className="rounded-md bg-primary/5 border border-primary/20 px-3 py-2 text-xs text-primary flex items-center gap-2">
                    <Check className="w-3.5 h-3.5 shrink-0" />
                    <span>
                      <strong>{formatTime12(form.time)}</strong> with {selectedProvider?.name} on {formatDateLabel(form.date)}
                      {selectedVt && <span className="text-primary/70"> ({selectedVt.name}, {selectedVt.duration_minutes}min)</span>}
                    </span>
                  </div>
                )}

                {/* Submit */}
                <Button
                  className="w-full h-9"
                  onClick={handleSubmit}
                  disabled={submitting || !form.patient_id || !form.provider_id || !form.time || !form.visit_type}
                  data-testid="appointment-create-submit"
                >
                  {submitting ? 'Scheduling...' : 'Schedule Appointment'}
                </Button>
              </div>
            </ScrollArea>
          </div>

          {/* RIGHT: Schedule Preview */}
          <div className="w-64 border-l bg-muted/10 flex flex-col shrink-0" data-testid="schedule-preview-panel">
            {previewLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-xs text-muted-foreground animate-pulse">Loading schedule...</div>
              </div>
            ) : (
              <MiniSchedulePreview
                providerId={form.provider_id}
                providerName={selectedProvider?.name}
                providerColor={selectedProvider?.color}
                dateStr={form.date}
                appointments={previewAppts}
                template={previewTemplate}
                availableSlots={availableSlots}
                bookedSlots={bookedSlots}
                selectedTime={form.time}
                selectedDuration={selectedVt?.duration_minutes || form.duration_minutes}
                onSlotClick={handleSlotClick}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
